import React from 'react';
import { Check, PlusCircle } from 'lucide-react';
import { Feature, QuestionnaireData } from '../../types';

interface FeaturesStepProps {
  data: Partial<QuestionnaireData>;
  onChange: (data: Partial<QuestionnaireData>) => void;
}

const features = [
  { id: 'ease-of-use' as Feature, label: 'Ease of use' },
  { id: 'integrations' as Feature, label: 'Integration with other tools' },
  { id: 'automation' as Feature, label: 'Automation capabilities' },
  { id: 'mobile' as Feature, label: 'Mobile accessibility' },
  { id: 'support' as Feature, label: 'Customer support' },
  { id: 'security' as Feature, label: 'Security features' },
  { id: 'scalability' as Feature, label: 'Scalability' },
  { id: 'scheduling' as Feature, label: 'Scheduling & dispatch' },
  { id: 'invoicing' as Feature, label: 'Invoicing & payments' },
  { id: 'job-tracking' as Feature, label: 'Job & project tracking' },
  { id: 'field-service' as Feature, label: 'Field service management' },
];

const MAX_SELECTIONS = 4;

export function FeaturesStep({ data, onChange }: FeaturesStepProps) {
  const selectedFeatures = data.features || [];
  const [customFeature, setCustomFeature] = React.useState('');

  const toggleFeature = (feature: Feature) => {
    if (selectedFeatures.includes(feature)) {
      // Remove the feature
      const newFeatures = selectedFeatures.filter(f => f !== feature);
      onChange({ features: newFeatures });
    } else if (selectedFeatures.length < MAX_SELECTIONS) {
      // Add the feature only if under the limit
      const newFeatures = [...selectedFeatures, feature];
      onChange({ features: newFeatures });
    }
  };

  const handleCustomFeature = () => {
    if (customFeature.trim() && selectedFeatures.length < MAX_SELECTIONS) {
      const newFeature = customFeature.trim() as Feature;
      onChange({ features: [...selectedFeatures, newFeature] });
      setCustomFeature('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">Select up to 4 features</p>
        <span className="text-sm font-medium text-blue-600">
          {selectedFeatures.length}/{MAX_SELECTIONS}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {features.map((feature) => {
          const isSelected = selectedFeatures.includes(feature.id);
          const isDisabled = !isSelected && selectedFeatures.length >= MAX_SELECTIONS;

          return (
            <button
              key={feature.id}
              onClick={() => toggleFeature(feature.id)}
              disabled={isDisabled}
              className={`p-3 sm:p-4 rounded-lg border-2 flex items-center transition-all text-left min-h-[48px] ${
                isSelected
                  ? 'border-blue-600 bg-blue-50'
                  : isDisabled
                  ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                  : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
              }`}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 flex-shrink-0 ${
                isSelected
                  ? 'border-blue-600 bg-blue-600'
                  : 'border-gray-300'
              }`}>
                {isSelected && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <span className={`text-sm sm:text-base text-gray-700 ${isDisabled ? 'opacity-50' : ''}`}>
                {feature.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 sm:mt-6">
        <p className="text-sm text-gray-600 mb-2">Need a specific feature? Add it here:</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customFeature}
            onChange={(e) => setCustomFeature(e.target.value)}
            placeholder="Enter desired feature"
            className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            disabled={selectedFeatures.length >= MAX_SELECTIONS}
          />
          <button
            onClick={handleCustomFeature}
            disabled={!customFeature.trim() || selectedFeatures.length >= MAX_SELECTIONS}
            className="p-2.5 text-blue-600 hover:text-blue-700 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default FeaturesStep;